import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, firstValueFrom, map, of, switchMap, take } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { TokenService } from '../services/token/token.service';
import { UserRoleService } from '../services/user-role/user-role.service';

@Injectable({
  providedIn: 'root'
})
export class JwtGuard implements CanActivate {

  constructor(
    private authService: AuthService, 
    private tokenService: TokenService,
    private router: Router,
    private userRoleService: UserRoleService
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    // First ensure we have a valid token (handles refresh if needed)
    const tokenValid = await this.tokenService.ensureValidToken();
    
    if (!tokenValid) {
      // Redirect to login page if token validation failed
      this.router.navigate(['/auth/login']);
      return false;
    }
    
    // If token is valid, ensure user data is loaded before proceeding
    return firstValueFrom(this.userRoleService.currentUser$.pipe(
      take(1),
      switchMap(currentUser => {
        // If user data is null, we need to load it
        if (currentUser === null) {
          return this.waitForUserDataObservable();
        }
        
        // User data is already loaded, proceed
        return of(true);
      })
    ));
  }
  
  private waitForUserDataObservable(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.userRoleService.loadCurrentUser()
        .then(() => {
          // User data loaded successfully
          observer.next(true);
          observer.complete();
        })
        .catch(error => {
          console.error('Error loading user data in JWT guard:', error);
          // If we can't load user data but token is valid, still allow access
          // The app can handle missing user data in components if needed
          observer.next(true);
          observer.complete();
        });
    });
  }
}
